import {
  Field,
  SingleSelectDropdown,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { appTextModes } from "@/shared/ui/text";
import type { InspectChannelDeclaration } from "../../../domain/episodeInspect";
import { repeatedColumnIndexes, type Selection } from "../../../domain/mapping";
import styles from "./MappingForm.module.css";

const NOT_SELECTED = "";

type SelectionFieldsProps = {
  /** 채널 드롭다운의 이름. 예) `위치 채널`. */
  label: string;
  selection: Selection;
  /** 이 episode 의 vector 채널 선언. */
  channels: readonly InspectChannelDeclaration[];
  /** 열 드롭다운마다의 이름. 예) `x · y · z`. 길이가 곧 열 수다. */
  columnLabels: readonly string[];
  onChannel: (channel: string | null) => void;
  onColumn: (index: number, column: number | null) => void;
  /** 채널과 열 사이에 설 칸. 예) 자세의 표현 · 순서 — 열 이름이 그 값을 따른다. */
  children?: ReactNode;
};

function channelOptions(
  channels: readonly InspectChannelDeclaration[],
  selected: string | null,
): SingleSelectDropdownOption[] {
  const options: SingleSelectDropdownOption[] = [
    { value: NOT_SELECTED, label: "선택 안 함" },
    ...channels.map((channel) => ({
      value: channel.key,
      label: `${channel.key} · ${channel.dim}차원`,
    })),
  ];
  // 저장된 매핑이 이 episode 에 없는 채널을 가리킬 수 있다. 값이 사라지지 않게 선택지로 남긴다.
  if (selected !== null && !channels.some((channel) => channel.key === selected)) {
    options.push({ value: selected, label: `${selected} · 이 episode 에 없음` });
  }
  return options;
}

function columnOptions(
  declaration: InspectChannelDeclaration | undefined,
): SingleSelectDropdownOption[] {
  if (declaration === undefined) {
    return [];
  }
  return Array.from({ length: declaration.dim }, (_, index) => {
    const name = declaration.names?.[index];
    return { value: String(index), label: name === undefined ? `${index}` : `${index} · ${name}` };
  });
}

/**
 * 원본 채널 하나와 열을 고르는 칸. 채널을 고르면 열은 비어 있다 — 사람이 고른다(자동으로 채우지 않는다).
 * `선택 안 함` 은 채널과 열을 비운다.
 */
export function SelectionFields({
  channels,
  children,
  columnLabels,
  label,
  onChannel,
  onColumn,
  selection,
}: SelectionFieldsProps) {
  const { channel } = selection;
  const declaration = channels.find((item) => item.key === channel);
  const options = columnOptions(declaration);
  const repeated = repeatedColumnIndexes(selection.columns);
  return (
    <>
      <Field label={label} layout="stack" textVariant={appTextModes["body-normal"]}>
        <SingleSelectDropdown
          fullWidth
          label={label}
          options={channelOptions(channels, channel)}
          placeholder="채널을 고르세요"
          size="md"
          value={channel}
          onValueChange={(value) => onChannel(value === NOT_SELECTED ? null : value)}
        />
      </Field>
      {children}
      {columnLabels.length === 0 ? null : (
        <div className={styles.columns}>
          {columnLabels.map((columnLabel, index) => {
            const column = selection.columns[index] ?? null;
            return (
              <Field
                key={columnLabel}
                label={columnLabel}
                layout="inline"
                labelWidth="2.5rem"
                textVariant={appTextModes["body-normal"]}
              >
                <SingleSelectDropdown
                  fullWidth
                  label={`${label} ${columnLabel} 열`}
                  options={options}
                  placeholder="열"
                  emptyMessage="채널을 먼저 고르세요"
                  size="md"
                  value={column === null ? null : String(column)}
                  onValueChange={(value) => onColumn(index, Number(value))}
                />
              </Field>
            );
          })}
        </div>
      )}
      {repeated.length === 0 ? null : (
        <p className={styles.attention} role="status">
          같은 열을 두 번 골랐습니다:{" "}
          {repeated.map((index) => columnLabels[index] ?? `${index + 1}번째`).join(" · ")}. 서로
          다른 열을 골라 주세요.
        </p>
      )}
      {channel === null ? null : (
        <details className={styles.selection}>
          <summary>선택한 원본 보기</summary>
          <div>{channel}</div>
          {columnLabels.map((axis, index) => {
            const column = selection.columns[index];
            return (
              <div key={axis}>
                {axis}: {column == null ? "미선택" : (options[column]?.label ?? column)}
              </div>
            );
          })}
        </details>
      )}
    </>
  );
}
